import { useEffect, useState, useRef } from 'react';
import { createLogger } from '@/utils/logger';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTaxBinder } from '@/hooks/api/useTaxBinders';
import { taxBinderKeys } from '@/lib/query/options';
import { DocuSealEmbedded } from '@/components/signatures/DocuSealEmbedded';
import { DocumentUpload, type UploadedDocument, type DocumentUploadRef } from '@/components/documents/DocumentUpload';
import { useDownloadDocument } from '@/hooks/api/useDocuments';
import { useUploadSignedEngagementLetter } from '@/hooks/api/useUploadSignedEngagementLetter';
import { DocumentType } from '@ata-digital/client-tax-system-api-client';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  Divider,
  Avatar,
  Collapse,
} from '@mui/material';
import {
  ArrowBack,
  Description,
  CheckCircle,
  HourglassEmpty,
  Lock,
  Person,
  Email,
  ArrowForward,
  Download as DownloadIcon,
  CloudUpload as UploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { QueryErrorBoundary } from '@/components/query/QueryErrorBoundary';
import { SIGNED_DOCUMENT_MIME_TYPES } from '@/constants/fileUpload';

const log = createLogger('EngagementLetterSigningPage');

export const Route = createFileRoute('/tax-binders/$binderId/engagement-letter')({
  component: EngagementLetterSigningPage,
  errorComponent: QueryErrorBoundary,
});

/**
 * Engagement Letter Signing Page (Client View)
 *
 * Allows clients to sign their engagement letter using DocuSeal embedded forms
 * or by downloading, printing, signing, and uploading a physical copy.
 */
function EngagementLetterSigningPage() {
  const { binderId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: binder, isLoading, error } = useTaxBinder(binderId);
  const [signatureCompleted, setSignatureCompleted] = useState(false);
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const documentUploadRef = useRef<DocumentUploadRef>(null);

  // Hooks for physical signature flow
  const downloadMutation = useDownloadDocument();
  const uploadMutation = useUploadSignedEngagementLetter();

  // Convenience accessor for engagement letter e-signature status
  const engagementLetter = binder?.engagementLetter;

  // Check if physical signature is allowed (from client settings)
  const physicalSignatureAllowed = engagementLetter?.physicalSignatureAllowed ?? false;

  // Debug logging
  useEffect(() => {
    if (binder && engagementLetter) {
      log.debug('Binder Data:', {
        hasDocument: engagementLetter.hasDocument,
        isSigned: engagementLetter.isSigned,
        hasSubmission: engagementLetter.hasSubmission,
        signingUrl: engagementLetter.signingUrl,
        submissionStatus: engagementLetter.submissionStatus,
        currentUserCanSign: engagementLetter.currentUserCanSign,
        currentUserSigningUrl: engagementLetter.currentUserSigningUrl,
        currentUserWaitingForPreviousSigner: engagementLetter.currentUserWaitingForPreviousSigner,
        currentUserSigned: engagementLetter.currentUserSigned,
        sequentialStatus: engagementLetter.sequentialStatus,
        waitingForSignerName: engagementLetter.waitingForSignerName,
        physicalSignatureAllowed: engagementLetter.physicalSignatureAllowed,
        engagementLetterRequired: engagementLetter.engagementLetterRequired,
        signatureMethod: engagementLetter.signatureMethod,
      });
    }
  }, [binder, engagementLetter]);

  // Auto-redirect after successful signature
  useEffect(() => {
    if (signatureCompleted) {
      const timer = setTimeout(() => {
        // Invalidate cached binder data so the detail page fetches fresh state
        queryClient.invalidateQueries({ queryKey: taxBinderKeys.detail(binderId) });
        navigate({ to: '/tax-binders/$binderId', params: { binderId } });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [signatureCompleted, navigate, binderId, queryClient]);

  const handleBack = () => {
    navigate({ to: '/tax-binders/$binderId', params: { binderId } });
  };

  const handleComplete = () => {
    setSignatureCompleted(true);
  };

  const handleDownload = async () => {
    if (!engagementLetter?.documentId) return;

    try {
      await downloadMutation.mutateAsync({
        documentId: engagementLetter.documentId,
        fileName: `Engagement Letter - ${binder?.name || 'Binder'}.pdf`,
      });
    } catch (error) {
      log.error('Failed to download engagement letter:', error);
    }
  };

  const handleUpload = async (documents: UploadedDocument[]) => {
    if (documents.length === 0) return;

    setUploadError(null);
    const file = documents[0].file;

    try {
      await uploadMutation.mutateAsync({
        binderId,
        file,
        comment: 'Physically signed engagement letter',
      });
      handleComplete();
    } catch (error) {
      log.error('Failed to upload signed engagement letter:', error);
      const errorMessage = error instanceof Error ? error.message : 'Upload failed. Please try again.';
      setUploadError(errorMessage);
      throw error;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !binder) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load tax binder information</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Tax Binder
        </Button>
      </Box>
    );
  }

  // No engagement letter
  if (!engagementLetter?.hasDocument) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">This tax binder does not have an engagement letter</Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Tax Binder
        </Button>
      </Box>
    );
  }

  // Current user already signed their portion
  if (engagementLetter.currentUserSigned) {
    const isFullyComplete =
      engagementLetter.isSigned ||
      engagementLetter.submissionStatus === 'completed' ||
      !engagementLetter.waitingForSignerName;

    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="success" icon={<CheckCircle />}>
          You have signed the engagement letter.
          {isFullyComplete
            ? ` All parties completed signing${engagementLetter.signedAt ? ` on ${new Date(engagementLetter.signedAt).toLocaleDateString()}` : ''}.`
            : ` Waiting for ${engagementLetter.waitingForSignerName} to sign. You will be notified when all parties have signed.`}
        </Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Tax Binder
        </Button>
      </Box>
    );
  }

  // Current user is waiting for a previous signer (sequential signing)
  if (engagementLetter.currentUserWaitingForPreviousSigner) {
    const waitingForName = engagementLetter.waitingForSignerName || 'Primary Taxpayer';

    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 4, md: 8 },
          px: 2,
        }}
      >
        <Card sx={{ maxWidth: 600, width: '100%', borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ width: 80, height: 80, bgcolor: 'info.light', mb: 2 }}>
                <HourglassEmpty sx={{ fontSize: 40, color: 'info.dark' }} />
              </Avatar>
              <Typography variant="h5" fontWeight={700} textAlign="center" gutterBottom>
                Waiting for Signature
              </Typography>
              <Typography variant="body1" color="text.secondary" textAlign="center">
                The engagement letter requires signatures in order.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                Signing Order
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: { xs: 1, sm: 2 },
                  flexWrap: 'wrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'warning.lighter',
                    border: '2px solid',
                    borderColor: 'warning.main',
                    minWidth: 140,
                  }}
                >
                  <Avatar sx={{ bgcolor: 'warning.main', mb: 1 }}>
                    <Person />
                  </Avatar>
                  <Typography variant="subtitle2" fontWeight={600} textAlign="center">
                    {waitingForName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <HourglassEmpty sx={{ fontSize: 16, color: 'warning.dark' }} />
                    <Typography variant="caption" color="warning.dark" fontWeight={500}>
                      Pending
                    </Typography>
                  </Box>
                </Box>

                <ArrowForward sx={{ color: 'text.disabled', fontSize: 28 }} />

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'grey.100',
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    minWidth: 140,
                  }}
                >
                  <Avatar sx={{ bgcolor: 'grey.400', mb: 1 }}>
                    <Person />
                  </Avatar>
                  <Typography variant="subtitle2" fontWeight={600} textAlign="center">
                    You
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Lock sx={{ fontSize: 16, color: 'text.disabled' }} />
                    <Typography variant="caption" color="text.disabled" fontWeight={500}>
                      Waiting
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                p: 2,
                bgcolor: 'info.lighter',
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Email sx={{ color: 'info.main', mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  You&apos;ll be notified
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Once {waitingForName} signs the engagement letter, you&apos;ll receive an email notification with a
                  link to complete your signature.
                </Typography>
              </Box>
            </Box>

            <Button variant="outlined" fullWidth onClick={handleBack} size="large">
              Back to Tax Binder
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Get the signing URL
  const signingUrl = engagementLetter.currentUserSigningUrl || engagementLetter.signingUrl;
  const canSign = engagementLetter.currentUserCanSign ?? (engagementLetter.hasSubmission && !!signingUrl);

  // No submission created yet or user cannot sign
  if (!engagementLetter.hasSubmission || !canSign || !signingUrl) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" icon={<Lock />}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Engagement Letter Not Available
          </Typography>
          <Typography variant="body2">
            {!engagementLetter.hasSubmission
              ? 'The engagement letter has not been sent for signature yet. Please contact your preparer to initiate the signing process.'
              : 'You are not able to sign the engagement letter at this time. Please contact your preparer for assistance.'}
          </Typography>
        </Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Tax Binder
        </Button>
      </Box>
    );
  }

  // Invalid signing URL
  if (signingUrl.endsWith('/s/') || signingUrl.length < 30) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          The signing URL is incomplete. Please contact your preparer to resend the engagement letter for signature.
        </Alert>
        <Button onClick={handleBack} sx={{ mt: 2 }}>
          Back to Tax Binder
        </Button>
      </Box>
    );
  }

  // Success state after signing
  if (signatureCompleted) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          Engagement Letter Signed!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Thank you for signing. Redirecting you back...
        </Typography>
      </Box>
    );
  }

  // Main signing view
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2, borderRadius: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack}>
            <ArrowBack />
          </IconButton>
          <Description sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h6">Sign Engagement Letter</Typography>
            <Typography variant="caption" color="text.secondary">
              {binder.name || 'Untitled Binder'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Sequential Status Banner */}
      {engagementLetter.sequentialStatus && engagementLetter.submissionStatus !== 'completed' && (
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, pt: 2 }}>
          <Alert severity="info" icon={<HourglassEmpty />}>
            <Typography variant="body2">
              <strong>Signing Status:</strong> {engagementLetter.sequentialStatus}
            </Typography>
          </Alert>
        </Box>
      )}

      {/* Instructions and Physical Signature Option */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, pt: 2 }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1" gutterBottom>
            Please review and sign the engagement letter below to begin your tax preparation process.
          </Typography>

          {physicalSignatureAllowed && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Alternatively, you can download the engagement letter, print it, sign it physically, and upload a copy
                of the signed document.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={downloadMutation.isPending ? <CircularProgress size={20} /> : <DownloadIcon />}
                  onClick={handleDownload}
                  disabled={downloadMutation.isPending || !engagementLetter.documentId}
                >
                  {downloadMutation.isPending ? 'Downloading...' : 'Download Engagement Letter'}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => setShowUploadSection(!showUploadSection)}
                  endIcon={showUploadSection ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                >
                  Upload Signed Copy
                </Button>
              </Box>

              {/* Upload Section */}
              <Collapse in={showUploadSection}>
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Upload your signed engagement letter
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Take a photo or scan of the signed document and upload it here.
                  </Typography>

                  {uploadError && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
                      {uploadError}
                    </Alert>
                  )}

                  {uploadMutation.isPending ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                      <CircularProgress sx={{ mb: 2 }} />
                      <Typography variant="body2" color="text.secondary">
                        Uploading signed document...
                      </Typography>
                    </Box>
                  ) : (
                    <DocumentUpload
                      ref={documentUploadRef}
                      onUpload={handleUpload}
                      maxFiles={1}
                      acceptedTypes={[...SIGNED_DOCUMENT_MIME_TYPES]}
                      documentType={DocumentType.EngagementLetter}
                      allowCamera={true}
                      allowMultiple={false}
                      autoUpload={true}
                      showPreviews={true}
                    />
                  )}
                </Box>
              </Collapse>
            </>
          )}
        </Paper>
      </Box>

      {/* DocuSeal Embedded Form */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
        <DocuSealEmbedded
          src={signingUrl}
          submissionId={engagementLetter.submissionId ? Number(engagementLetter.submissionId) : undefined}
          onComplete={handleComplete}
        />
      </Box>
    </Box>
  );
}
